"use client";

/**
 * ConsentBanner — first-visit consent banner.
 *
 * Renders when no consent record exists in localStorage. Accept and Reject
 * are visually equivalent (same size, same weight) — the design tokens do
 * NOT distinguish them by colour weight to avoid a dark-pattern reading.
 *
 * Fires no analytics and sets no non-essential storage until the user
 * chooses. Emits a `sc-consent-change` window event via `setAnalyticsConsent`,
 * which the analytics loaders and A/B test listen for.
 *
 * Accessibility:
 *   - Rendered as `role="region"` with an aria-label — NOT a modal dialog:
 *     it does not block the page, does not trap focus, does not require an
 *     acknowledgement to keep browsing. This matches EDPB guidance that
 *     the site must remain usable when the user has not answered yet.
 *   - Buttons are focusable in DOM order and dismissable with Enter/Space.
 *   - AA contrast against the surface background (verified with existing
 *     border/text tokens used across the rest of the site).
 */

import { useEffect, useState } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import {
  readConsent,
  setAnalyticsConsent,
  clearNonEssentialStorage,
  CONSENT_CHANGE_EVENT,
} from "@/lib/consent";
import { openConsentPreferences } from "./ConsentPreferenceCenter";

export function ConsentBanner() {
  const { t, language } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only when there is no prior record. Reading localStorage is a
    // client-only side effect — SSR renders return `null` for visible.
    if (readConsent() === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: syncing SSR-null state with client-side localStorage on mount
      setVisible(true);
    }

    // If the user opens the preference centre and saves, this listener
    // hides the banner too.
    const onChange = () => {
      if (readConsent() !== null) setVisible(false);
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
  }, []);

  if (!visible) return null;

  const privacyHref = language === "bg" ? "/bg/legal/privacy" : "/legal/privacy";

  return (
    <div
      role="region"
      aria-label={t("consent_banner_aria_label")}
      className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 pointer-events-none"
    >
      <div className="mx-auto max-w-4xl pointer-events-auto rounded-xl border border-border bg-surface/95 backdrop-blur shadow-lg p-4 sm:p-5">
        <div className="flex flex-col gap-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary mb-1">
              {t("consent_banner_title")}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {t("consent_banner_body")}{" "}
              <Link
                href={privacyHref}
                className="underline hover:text-text-primary"
              >
                {t("consent_banner_learn_more")}
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => {
                clearNonEssentialStorage();
                setAnalyticsConsent("denied");
              }}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t("consent_banner_reject")}
            </button>
            <button
              type="button"
              onClick={() => openConsentPreferences()}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t("consent_banner_preferences")}
            </button>
            <button
              type="button"
              onClick={() => setAnalyticsConsent("granted")}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-hover-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t("consent_banner_accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
